import { useCallback } from 'react';
import useAppDispatch from '@sportson/core-web/hooks/useAppDispatch';
import { useTranslation } from 'react-i18next';
import { close as closeOverlay, show as showOverlay } from '@sportson/core-web/state/application/overlay';
import Image from '@sportson/core-web/components/Image';
import TouchableSlider from '@sportson/core-web/components/sliders/TouchableSlider';
import styled from 'libs/styled';
import Button from 'components/Button';
import { ArrowIcon } from 'assets/media/icons/ArrowIcon';
import { above } from '@sportson/core-web/utils/mediaqueries';
import { stormSrc, generateSrcSet } from '@sportson/core-web/libs/image';
import Text from 'components/Text';
import GalleryCard from './GalleryCard';
import GalleryThumbnails from './GalleryThumbnails';
import usePdpSlider from './usePdpSlider';

const GalleryWrapper = styled('div')`
    display: flex;
    mix-blend-mode: multiply;
    overflow: hidden;
    position: relative;
    flex: 1;

    ${above['tablet.md']} {
        padding-bottom: 40px 0;
    }

    .slider-component-wrapper {
        width: 100%;
    }
`;

const ButtonWrapper = styled('div')`
    position: absolute;
    z-index: var(--zindex-middleground);
    height: 100%;
    align-items: center;
`;

const Slide = styled('div')`
    display: flex;
    align-items: center;
    justify-content: center;
    border: none;

    ${above['tablet.md']} {
        cursor: pointer;
        cursor:
            url('/CursorOpenGallery.svg') 20 20,
            pointer;
    }
`;

const GalleryThumbnailsWrapper = styled('div')`
    width: 100%;

    -ms-overflow-style: none; /* Internet Explorer 10+ */
    scrollbar-width: none; /* Firefox */
    &::-webkit-scrollbar {
        display: none; /* Safari and Chrome */
    }
`;

const ImageWrapper = styled('div')`
    width: 100%;
    padding: 0 16px;
`;

const ImageZoomWrapper = styled('div')`
    ${above['tablet.md']} {
        cursor: pointer;
        cursor:
            url('/CursorOpenGallery.svg') 20 20,
            pointer;
    }
`;

const MediaGallery = ({ media }) => {
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const [sliderProps, sliderOptions] = usePdpSlider();
    const { activeIndex, slideTo } = sliderOptions;

    const uniqueMedia = media.filter((media, index, self) => self.findIndex((t) => t.image === media.image) === index);
    const mainMedia = uniqueMedia[0];
    const sizes = ['100vw', null, null, '50vw'];
    const widths = [360, 460, 560, 660, 760, 860, 960, 1060, 1160, 1260, 1360];

    const isSlider = media.length > 1;
    const isImage = !!media[activeIndex]?.image;
    const imageKey = mainMedia.key;
    const imageSrc = stormSrc(`${imageKey}.jpg`, { auto: 'format', q: 80, ar: '1', fit: 'fill' });
    const srcSet = generateSrcSet(imageSrc, widths);

    const onImageClick = useCallback(() => {
        const data = { media: uniqueMedia, startIndex: activeIndex };
        dispatch(showOverlay({ view: 'galleryZoomOverlay', transition: 'fade', data }));
    }, [activeIndex, uniqueMedia]);

    return (
        <>
            <GalleryWrapper borderRadius={['var(--corner-radius-round, 4px)', null, null, 'unset']}>
                {isSlider ? (
                    <>
                        <ButtonWrapper left="16px" display={['none', null, null, 'flex']}>
                            <Button
                                size="xsmall"
                                theme="secondary"
                                onClick={() => slideTo(activeIndex - 1 < 0 ? media.length - 1 : activeIndex - 1)}
                                aria-label={t('pdp.gallery.previous_image')}
                            >
                                <ArrowIcon direction="left" />
                            </Button>
                        </ButtonWrapper>
                        <ButtonWrapper right="16px" display={['none', null, null, 'flex']}>
                            <Button
                                size="xsmall"
                                theme="secondary"
                                onClick={() => slideTo(activeIndex + 1 < 0 ? media.length - 1 : activeIndex + 1)}
                                aria-label={t('pdp.gallery.next_image')}
                            >
                                <ArrowIcon />
                            </Button>
                        </ButtonWrapper>
                        <TouchableSlider
                            {...sliderProps}
                            className="cursor"
                            isInfinite
                            onTap={isImage && onImageClick}
                            aria-label={t('pdp.gallery.click_to_zoom')}
                        >
                            {uniqueMedia.map((media, index) => (
                                <Slide key={`media-${index}-${media.key}`}>
                                    <GalleryCard media={media} isLazyLoading={index !== 0} />
                                </Slide>
                            ))}
                        </TouchableSlider>
                    </>
                ) : (
                    <ImageWrapper>
                        {!imageKey ? (
                            <Text typography="UI/12_100_0_350">{t('image_not_available')}</Text>
                        ) : (
                            <ImageZoomWrapper
                                onClick={onImageClick}
                                onKeyDown={(e) => e.key === 'Enter' && onImageClick()}
                                role="button"
                                tabIndex={0}
                                aria-label={t('pdp.gallery.click_to_zoom')}
                                style={{ height: '100%' }}
                            >
                                <Image
                                    key={mainMedia.title}
                                    draggable={false}
                                    src={{ url: imageSrc, width: widths }}
                                    srcSet={srcSet}
                                    sizes={sizes}
                                    fallbackImageAsPlaceholder={false}
                                    alt={mainMedia.title}
                                    title={mainMedia.title}
                                    loading="lazy"
                                />
                            </ImageZoomWrapper>
                        )}
                    </ImageWrapper>
                )}
            </GalleryWrapper>
            {isSlider && (
                <GalleryThumbnailsWrapper maxWidth={['calc(100vw)', null, null, '50vw']}>
                    <GalleryThumbnails activeIndex={activeIndex} media={uniqueMedia} onClick={slideTo} />
                </GalleryThumbnailsWrapper>
            )}
        </>
    );
};

MediaGallery.propTypes = {};

export default MediaGallery;
